import React from "react";
import { Link } from "react-router-dom";
import { FiTarget, FiEye, FiAward, FiUsers, FiArrowRight } from "react-icons/fi";

const teamMembers = [
  { name: "Jean Baptiste", role: "CEO & Founder", initial: "JB", desc: "10+ years in Kigali real estate" },
  { name: "Marie Claire", role: "Head of Sales", initial: "MC", desc: "Expert in luxury properties" },
  { name: "Patrick Mugabo", role: "Lead Agent", initial: "PM", desc: "Specializes in residential homes" },
  { name: "Alice Uwimana", role: "Marketing Director", initial: "AU", desc: "Digital marketing expert" },
];

const values = [
  { icon: <FiTarget />, title: "Our Mission", desc: "To make property discovery in Kigali seamless, transparent, and accessible for everyone." },
  { icon: <FiEye />, title: "Our Vision", desc: "To become Rwanda's most trusted digital platform for real estate, connecting people with their dream homes." },
  { icon: <FiAward />, title: "Our Values", desc: "Integrity, innovation, and customer-first approach guide everything we do at Home Discovery." },
  { icon: <FiUsers />, title: "Our Community", desc: "We've helped 200+ families find their perfect homes across Kigali's vibrant neighborhoods." },
];

export default function About() {
  return (
    <div className="page-wrapper">
      {/* Page Header */}
      <div className="page-header">
        <div className="container position-relative text-center">
          <span className="badge bg-primary text-white fw-semibold px-3 py-2 rounded-pill mb-3">
            About Us
          </span>
          <h1>Discover Who We Are</h1>
          <p style={{ maxWidth: 600, margin: "0 auto" }}>
            Home Discovery is Kigali's premier real estate platform, connecting property seekers
            with their perfect homes since 2020.
          </p>
        </div>
      </div>

      {/* Values */}
      <section className="py-5">
        <div className="container">
          <div className="row g-4">
            {values.map((v, i) => (
              <div className="col-md-6 col-lg-3" key={i}>
                <div className="stat-card text-center p-4 h-100">
                  <div className={`stat-icon ${["blue", "green", "amber", "purple"][i]} mx-auto mb-3`}>
                    {v.icon}
                  </div>
                  <h5 className="fw-bold">{v.title}</h5>
                  <p className="text-muted small mb-0">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-5" style={{ background: "var(--gray-50)" }}>
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <span className="badge bg-primary text-white fw-semibold px-3 py-2 rounded-pill mb-3">
                Our Story
              </span>
              <h2 className="fw-bold mb-4" style={{ color: "var(--secondary)" }}>
                Building Kigali's Future, One Home at a Time
              </h2>
              <p className="text-muted" style={{ lineHeight: 1.8 }}>
                Founded in 2020, Home Discovery started with a simple vision: to transform
                how people find homes in Kigali. We noticed a gap between property seekers
                and available listings, and set out to create a platform that bridges that gap.
              </p>
              <p className="text-muted" style={{ lineHeight: 1.8 }}>
                Today, we list properties across all of Kigali's neighborhoods, from the
                luxury residences of Nyarutarama to the vibrant streets of Nyamirambo.
                Our platform serves hundreds of home seekers monthly, offering verified
                listings, professional agents, and a seamless search experience.
              </p>
              <div className="d-flex gap-4 mt-4">
                <div>
                  <h3 className="fw-bold text-primary mb-0">200+</h3>
                  <small className="text-muted">Homes Sold</small>
                </div>
                <div>
                  <h3 className="fw-bold text-primary mb-0">50+</h3>
                  <small className="text-muted">Active Listings</small>
                </div>
                <div>
                  <h3 className="fw-bold text-primary mb-0">15+</h3>
                  <small className="text-muted">Neighborhoods</small>
                </div>
                <div>
                  <h3 className="fw-bold text-primary mb-0">5★</h3>
                  <small className="text-muted">Client Rating</small>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="p-3 rounded-4" style={{ background: "var(--gradient-primary)" }}>
                <div className="row g-3">
                  {[1, 2, 3, 4].map((n) => (
                    <div className="col-6" key={n}>
                      <div
                        className="rounded-3 d-flex align-items-center justify-content-center"
                        style={{
                          height: 150,
                          background: `rgba(255,255,255,${0.1 + n * 0.05})`,
                          backdropFilter: "blur(10px)",
                        }}
                      >
                        <span style={{ fontSize: "2.5rem" }}>
                          {["🏠", "🏢", "🏡", "🏘️"][n - 1]}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <span className="badge bg-primary text-white fw-semibold px-3 py-2 rounded-pill mb-3">
              Our Team
            </span>
            <h2 className="fw-bold" style={{ color: "var(--secondary)" }}>
              Meet the Experts
            </h2>
            <p className="text-muted">The dedicated people behind Home Discovery</p>
          </div>
          <div className="row g-4">
            {teamMembers.map((member, i) => (
              <div className="col-md-6 col-lg-3" key={i}>
                <div className="team-card">
                  <div className="team-avatar">{member.initial}</div>
                  <h5 className="fw-bold mb-1">{member.name}</h5>
                  <p className="text-primary small fw-semibold mb-2">{member.role}</p>
                  <p className="text-muted small mb-0">{member.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-5" style={{ background: "var(--gray-50)" }}>
        <div className="container text-center">
          <h2 className="fw-bold mb-3" style={{ color: "var(--secondary)" }}>
            Ready to Find Your Dream Home?
          </h2>
          <p className="text-muted mb-4" style={{ maxWidth: 500, margin: "0 auto" }}>
            Join hundreds of happy homeowners who found their perfect property through Home Discovery.
          </p>
          <div className="d-flex justify-content-center gap-3">
            <Link to="/properties" className="btn btn-primary rounded-pill px-4 py-2 fw-semibold">
              Browse Properties <FiArrowRight className="ms-1" />
            </Link>
            <Link to="/contact" className="btn btn-outline-primary rounded-pill px-4 py-2 fw-semibold">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
